/**
 * InstallPromptStep (session 126s) — final tour step (6 of 6) that
 * walks the user through pinning PeopleiKnow to their home screen.
 *
 * Unlike the first 5 steps, this one has NO in-page DOM target (the
 * Share button / browser menu lives in the browser chrome, not in
 * the React tree), so it renders as a centered modal card on top of
 * the existing tour backdrop rather than the spotlight-with-cutout.
 *
 * Platform detection lives in lib/detectPlatform; we just consume it
 * to pick the right step list. When the app is ALREADY running as a
 * standalone PWA we show a celebratory "you're all set" panel instead
 * of the install instructions.
 *
 * data-testids:
 *   install-prompt-modal
 *   install-prompt-title
 *   install-prompt-finish-btn
 *   install-prompt-back-btn
 *   install-prompt-skip-btn
 *   install-step-{n}
 */
import { useMemo } from 'react';
import { Button } from '../ui/button';
import {
  Share, MoreHorizontal, Smartphone, Monitor, CheckCircle2,
  ArrowLeft, X,
} from 'lucide-react';
import { detectPlatform } from '../../lib/detectPlatform';

function getInstructions(platform) {
  // Each step has a small icon + the spoken instruction. iOS Safari
  // matches the user's screenshot reference: Share -> View More ->
  // Add to Home Screen.
  if (platform === 'installed') {
    return {
      headline: "You're all set!",
      lede: 'PeopleiKnow is already on your home screen — tap the icon any time to launch the app full-screen.',
      steps: [],
    };
  }
  if (platform === 'ios-safari') {
    return {
      headline: 'Add PeopleiKnow to your home screen',
      lede: 'On iPhone, this gives you a full-screen icon just like a native app.',
      steps: [
        {
          icon: Share,
          title: 'Tap the Share button',
          body: 'It\u2019s the square-with-an-up-arrow icon at the bottom of Safari.',
        },
        {
          icon: MoreHorizontal,
          title: 'Scroll down and tap "Add to Home Screen"',
          body: 'You\u2019ll see it in the row of action icons below the share targets.',
        },
        {
          icon: Smartphone,
          title: 'Tap "Add" in the top-right',
          body: 'PeopleiKnow now lives on your home screen — tap to open.',
        },
      ],
    };
  }
  if (platform === 'ios-chrome' || platform === 'ios-other') {
    return {
      headline: 'Open this page in Safari first',
      lede: 'Chrome on iPhone can\u2019t add apps to your home screen — Safari is the only browser Apple lets do that.',
      steps: [
        {
          icon: Share,
          title: 'Tap the Share button in your browser',
        },
        {
          icon: Smartphone,
          title: 'Choose "Open in Safari"',
          body: 'Then come back to the tour and we\u2019ll walk you through the Safari steps.',
        },
      ],
    };
  }
  if (platform === 'android') {
    return {
      headline: 'Install PeopleiKnow on Android',
      lede: 'Chrome and Edge install PeopleiKnow as a full-screen app in two taps.',
      steps: [
        {
          icon: MoreHorizontal,
          title: 'Tap the menu (\u22EE) in the top-right of your browser',
        },
        {
          icon: Smartphone,
          title: 'Choose "Install app" or "Add to home screen"',
          body: 'Some browsers label it "Install PeopleiKnow" \u2014 same thing.',
        },
        {
          icon: CheckCircle2,
          title: 'Tap "Install" / "Add" to confirm',
          body: 'The icon will appear on your home screen seconds later.',
        },
      ],
    };
  }
  // desktop
  return {
    headline: 'Install PeopleiKnow on your computer',
    lede: 'Chrome, Edge, and Brave can install PeopleiKnow as a standalone app on Windows / Mac / Linux.',
    steps: [
      {
        icon: Monitor,
        title: 'Look for the install icon in the address bar',
        body: 'A small monitor-with-arrow icon usually appears at the right end of the URL bar.',
      },
      {
        icon: MoreHorizontal,
        title: 'Or open the browser menu and pick "Install PeopleiKnow"',
        body: 'On Chrome it\u2019s under the three-dot menu \u2192 Save and Share \u2192 Install PeopleiKnow.',
      },
      {
        icon: CheckCircle2,
        title: 'Click "Install" to confirm',
        body: 'PeopleiKnow opens in its own window and appears in your taskbar / Dock.',
      },
    ],
  };
}

export default function InstallPromptStep({
  stepNumber,
  totalSteps,
  onBack,
  onFinish,
  onSkip,
}) {
  const platform = useMemo(() => detectPlatform(), []);
  const { headline, lede, steps } = useMemo(() => getInstructions(platform), [platform]);

  return (
    <div className="fixed inset-0 z-[1000]" data-testid="install-prompt-modal">
      {/* Backdrop — clicking it does NOT close (parity with the
          spotlight steps where the only exits are the buttons). */}
      <div className="absolute inset-0 bg-black/55" />

      {/* Modal card */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(420px,92vw)] max-h-[88vh] overflow-y-auto bg-card border border-border rounded-2xl shadow-2xl p-5"
      >
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            Tour &middot; {stepNumber} of {totalSteps}
          </span>
          <button
            type="button"
            onClick={onSkip}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Skip tour"
            data-testid="install-prompt-skip-btn"
          >
            <X size={14} />
          </button>
        </div>

        <h3
          className="font-heading text-lg font-semibold leading-tight mb-1"
          data-testid="install-prompt-title"
        >
          {headline}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
          {lede}
        </p>

        {platform === 'installed' ? (
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 flex items-start gap-3 mb-4">
            <CheckCircle2 size={22} className="text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-900">
              Nothing more to do here. The next time you open the app from your home screen, it&rsquo;ll launch full-screen.
            </p>
          </div>
        ) : (
          <ol className="space-y-3 mb-4">
            {steps.map((s, idx) => {
              const Icon = s.icon;
              return (
                <li
                  key={idx}
                  className="flex items-start gap-3"
                  data-testid={`install-step-${idx + 1}`}
                >
                  <span className="w-7 h-7 rounded-full bg-nav-blue/10 text-nav-blue flex items-center justify-center shrink-0 mt-0.5">
                    <Icon size={14} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                        Step {idx + 1}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-foreground leading-snug">
                      {s.title}
                    </p>
                    {s.body && (
                      <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                        {s.body}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        <div className="flex items-center gap-2 pt-1">
          <Button
            size="sm"
            variant="outline"
            onClick={onBack}
            className="rounded-full h-8 text-xs"
            data-testid="install-prompt-back-btn"
          >
            <ArrowLeft size={12} className="mr-1" /> Back
          </Button>
          <div className="flex-1" />
          <Button
            size="sm"
            onClick={onFinish}
            className="rounded-full h-8 text-xs bg-nav-blue hover:bg-nav-blue/80"
            data-testid="install-prompt-finish-btn"
          >
            Finish tour
          </Button>
        </div>
      </div>
    </div>
  );
}
